import React, { useEffect, useState, useRef } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { useRegisterPaymentMethod as useStyles } from './styles';

// Components(Material-UI)
import { Box, Button, RadioGroup, Grid, Radio, FormControlLabel } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';

// styles
import './payment.css';

// Utils
import CustomerPaymentComponent from './components/CustomerPaymentComponent';
import ChangePaymentMethodActions from './components/ChangePaymentMethodActions';
import { DEFAULT_CARD_LENGTH } from 'js/utils/helpers/const';

const PaymentForm = (props) => {
  const classes = useStyles();
  const childRef = useRef();

  const {
    isUpdateServicePlan,
    isQrLimitInPoints,
    paymentInfo,
    showWarningMessage,
    handleSuccess,
    handleChangeInput,
    handleChangeCheckbox,
    handleCloseModal,
    isErrorValidateRegisterForm,
    checkDownGradeServicePayment,
    cards,
    updateStripePaymentMethodId,
    selectedPlan
  } = props;
  const stripe = useStripe();
  const elements = useElements();

  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [showFormCreate, setShowFormCreate] = useState(false);
  const [cardInfo, setCardInfo] = useState(paymentInfo.stripe_payment_method_id);

  useEffect(() => {
    if (cards.length === 0) {
      // Case: default display form create new card
      setShowFormCreate(true);
    }
  });

  const handleSubmit = async (e) => {
    if (showFormCreate) {
      // Create new card for payment on Stripe
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      setIsLoading(true);
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {},
        redirect: 'if_required',
      });

      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment, details incomplete)
        showWarningMessage(error.message);
        childRef.current.closeModalConfirm();
      } else {
        await updateStripePaymentMethodId(setupIntent.payment_method);
        await handleSuccess();
      }

      setIsLoading(false);
    } else {
      handleSuccess();
    }
  };

  const cardHide = (cardLength, last4) => {
    let hideNum = [];
    const length = cardLength - 4;
    for (let i = 0; i < length; i++) {
      if (i % 4 === 0) {
        hideNum.push(` `);
      }
      hideNum.push('*');
    }

    return hideNum.join('') + ` ${last4}`;
  };

  const handleChangeRadioButton = (event) => {
    const { value } = event.target;
    setCardInfo(value);
  };

  return (
    <form>
      {(showFormCreate || cards.length === 0) && (
        <Box id='payment-form'>
          {isLoading ? (
            <Box display='flex' justifyContent='center' mt={2}>
              <CircularProgress />
            </Box>
          ) : null}
          <PaymentElement id='payment-element' onReady={() => setIsLoading(false)} />
        </Box>
      )}
      {(!showFormCreate && cards.length !== 0) && (
        <RadioGroup
          name='payment_method'
          onChange={(event) => handleChangeRadioButton(event)}
          value={cardInfo}
        >
          <Grid container spacing={2}>
            {
              cards.map(card => (
                <Grid item xs={6} sm={6}>
                  <Box
                    className={classes.card}
                    style={{ boxShadow: card.id == cardInfo && '5px 5px 10px rgb(0 0 0 / 15%)' }}
                    onClick={() => {
                      setCardInfo(card.id);
                      updateStripePaymentMethodId(card.id);
                    }}
                  >
                    <Box display='flex' justifyContent='space-between' fontWeight={600}>
                      <Box py={1} px={2}>
                        {card?.card_information?.brand?.charAt(0).toUpperCase() +
                          card?.card_information?.brand?.slice(1)}
                      </Box>
                      <FormControlLabel
                        value={card.id}
                        control={
                          <Radio 
                            disableRipple 
                            checked={paymentInfo?.stripe_payment_method_id === card?.id}
                            classes={{ checked: classes.radioChecked }} 
                          />
                        }
                      />
                    </Box>
                    <Box className={classes.cardNumber}>
                      {cardHide(DEFAULT_CARD_LENGTH, card.card_information.last4)}
                    </Box>
                    <Box className={classes.contentBottom}>
                      <Box ml={2}>
                        <Box fontSize={12} fontWeight={600}>
                          Valid Thru
                        </Box>
                        <Box fontWeight={600}>
                          {
                            (
                              card?.card_information?.exp_month?.toString().length === 1
                                ? '0' + card?.card_information?.exp_month
                                : card?.card_information?.exp_month
                            ) + '/' + card?.card_information?.exp_year?.toString().slice(-2)
                          }
                        </Box>
                      </Box>
                      <Box className={classes.mastercardContainer}>
                        <Box className={classes.mastercard}></Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                )
              )
            }
          </Grid>
        </RadioGroup>
      )}
      {
        cards.length !== 0 &&
        <Button
          variant='outlined'
          style={{
            color: 'rgb(242, 153, 74)',
            border: '2px solid rgb(242, 153, 74)',
            padding: '6px 15px',
          }}
          onClick={() => setShowFormCreate(!showFormCreate)}
        >
          {!showFormCreate ? '追加する' : '戻る'}
        </Button>
      }

      <CustomerPaymentComponent
        paymentInfo={paymentInfo}
        handleChangeInput={handleChangeInput}
        handleChangeCheckbox={handleChangeCheckbox}
      />

      <Box display='flex' justifyContent='center'>
        <ChangePaymentMethodActions
          isLoading={isLoading}
          isUpdateServicePlan={isUpdateServicePlan}
          paymentInfo={paymentInfo}
          handleSubmit={handleSubmit}
          onClose={handleCloseModal}
          isErrorValidateRegisterForm={isErrorValidateRegisterForm}
          checkDownGradeServicePayment={checkDownGradeServicePayment}
          isQrLimitInPoints={isQrLimitInPoints}
          showFormCreate={showFormCreate}
          showWarningMessage={showWarningMessage}
          ref={childRef}
          selectedPlan={selectedPlan}
        />
      </Box>
    </form>
  );
};

// PropTypes
PaymentForm.propTypes = {
  isUpdateServicePlan: PropTypes.bool,
  isQrLimitInPoints: PropTypes.bool,
  paymentInfo: PropTypes.object,
  cards: PropTypes.array,
  handleSuccess: PropTypes.func,
  showWarningMessage: PropTypes.func,
  handleChangeInput: PropTypes.func,
  handleChangeCheckbox: PropTypes.func,
  handleCloseModal: PropTypes.func,
  isErrorValidateRegisterForm: PropTypes.func,
  checkDownGradeServicePayment: PropTypes.func,
  updateStripePaymentMethodId: PropTypes.func,
  selectedPlan: PropTypes.object
};
PaymentForm.defaultProps = {
  isUpdateServicePlan: false,
  isQrLimitInPoints: false,
  paymentInfo: {},
  cards: [],
  handleSuccess: () => {},
  showWarningMessage: () => {},
  handleChangeInput: () => {},
  handleChangeCheckbox: () => {},
  handleCloseModal: () => {},
  isErrorValidateRegisterForm: () => {},
  checkDownGradeServicePayment: () => {},
  updateStripePaymentMethodId: () => {},
  selectedPlan: {}
};

export default PaymentForm;
